import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Lang, Method, Species } from "../types";
import { DICT, type Dictionary, type I18nKey } from "./dictionary";

const SUPPORTED_LANGS: Lang[] = ["de", "en", "is"];

function browserLocales(): string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages?.length) return [...navigator.languages];
  return navigator.language ? [navigator.language] : [];
}

function detectBrowserLang(): Lang {
  for (const loc of browserLocales()) {
    const code = String(loc).toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED_LANGS.includes(code as Lang)) return code as Lang;
  }
  return "en";
}

function readStoredLang(): Lang {
  if (typeof localStorage === "undefined") return detectBrowserLang();
  const stored = localStorage.getItem("fishing-lang");
  if (stored && SUPPORTED_LANGS.includes(stored as Lang)) return stored as Lang;
  return detectBrowserLang();
}

const SPECIES_KEY: Record<Species, I18nKey> = {
  salmon: "spcSalmon",
  brown_trout: "spcBrownTrout",
  arctic_char: "spcCharS",
  sea_trout: "spcSeaTrout",
  rainbow_trout: "spcRainbow",
};
const METHOD_KEY: Record<Method, I18nKey> = {
  fly: "mthFly",
  spinner: "mthSpinner",
  worm: "mthWorm",
  bait: "mthBait",
  lure: "mthLure",
};

interface LangCtx {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: (key: I18nKey) => string;
  speciesLabel: (s: Species) => string;
  methodLabel: (m: Method) => string;
  dict: Dictionary;
}

const Ctx = createContext<LangCtx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem("fishing-lang", next);
    } catch {
      /* ignore quota errors */
    }
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<LangCtx>(() => {
    const dict = DICT[lang] ?? DICT.de;
    const t = (key: I18nKey) => dict[key] ?? DICT.de[key] ?? String(key);
    return {
      lang,
      setLang,
      t,
      dict,
      speciesLabel: (s) => t(SPECIES_KEY[s] ?? ("" as I18nKey)) || s,
      methodLabel: (m) => t(METHOD_KEY[m] ?? ("" as I18nKey)) || m,
    };
  }, [lang, setLang]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLang must be inside LangProvider");
  return v;
}
